import { endOfMonth, format, startOfMonth } from "date-fns";
import { sortBy, uniq } from "lodash";
import { StaffWeeklyHours } from "../types";

export type MonthContractType = "contract" | "services_provider" | "cash";

export type ContractPeriod = {
    agreementType: string;
    contractType: MonthContractType;
    startDate: string;
    endDate: string;
};

const CONTRACT_TYPE_BY_WEEKLY_HOURS_TYPE: Record<string, MonthContractType> = {
    term_contract: "contract",
    without_term_contract: "contract",
    internship_iefp: "contract",
    services_provider: "services_provider",
    cash: "cash",
};

const CONTRACT_TYPE_BY_AGREEMENT_TYPE: Record<string, MonthContractType> = {
    term_contract: "contract",
    full_term: "contract",
    services_provider: "services_provider",
};

type MonthContractTypesInput = {
    weeklyHoursSchedule: Array<StaffWeeklyHours>;
    agreementType: string | null | undefined;
    monthDate: Date;
};

type MixedContractMonthWarningInput = {
    weeklyHoursSchedule: Array<StaffWeeklyHours>;
    monthDate: Date;
    agreementTypeLabels: Record<string, string>;
};

export const getWeeklyHoursContractType = (weeklyHoursType: string | null | undefined) => {
    const contractType: MonthContractType | undefined = CONTRACT_TYPE_BY_WEEKLY_HOURS_TYPE[weeklyHoursType ?? ""];
    return contractType;
};

const getMonthDateKeys = (monthDate: Date) => {
    const monthDateKeys = {
        monthStartKey: format(startOfMonth(monthDate), "yyyy-MM-dd"),
        monthEndKey: format(endOfMonth(monthDate), "yyyy-MM-dd"),
    };
    return monthDateKeys;
};

const getRowDateKeys = (scheduleRow: StaffWeeklyHours) => {
    const rowDateKeys = {
        rowStartKey: scheduleRow.date_start.slice(0, 10),
        rowEndKey: scheduleRow.date_end ? scheduleRow.date_end.slice(0, 10) : null,
    };
    return rowDateKeys;
};

const getTypedRowsCoveringMonth = (weeklyHoursSchedule: Array<StaffWeeklyHours>, monthDate: Date) => {
    const { monthStartKey, monthEndKey } = getMonthDateKeys(monthDate);
    const sortedSchedule = sortBy(weeklyHoursSchedule, (scheduleRow) => scheduleRow.date_start);
    const typedRowsCoveringMonth = sortedSchedule.filter((scheduleRow) => {
        const { rowStartKey, rowEndKey } = getRowDateKeys(scheduleRow);
        const coversMonth = rowStartKey <= monthEndKey && (rowEndKey === null || rowEndKey >= monthStartKey);
        const hasKnownType = Boolean(getWeeklyHoursContractType(scheduleRow.agreement_type));
        return coversMonth && hasKnownType;
    });
    return typedRowsCoveringMonth;
};

export const getMonthContractPeriods = (weeklyHoursSchedule: Array<StaffWeeklyHours>, monthDate: Date) => {
    const { monthStartKey, monthEndKey } = getMonthDateKeys(monthDate);
    const periods: Array<ContractPeriod> = getTypedRowsCoveringMonth(weeklyHoursSchedule, monthDate).map((scheduleRow) => {
        const { rowStartKey, rowEndKey } = getRowDateKeys(scheduleRow);
        const period = {
            agreementType: scheduleRow.agreement_type ?? "",
            contractType: CONTRACT_TYPE_BY_WEEKLY_HOURS_TYPE[scheduleRow.agreement_type ?? ""],
            startDate: rowStartKey > monthStartKey ? rowStartKey : monthStartKey,
            endDate: rowEndKey !== null && rowEndKey < monthEndKey ? rowEndKey : monthEndKey,
        };
        return period;
    });
    return periods;
};

// What the person was in the month: the types of the Horas Semanais rows covering any of its days, in the order
// they start. With no typed row covering it, the agreement's current "Tipo de contrato" decides.
export const getMonthContractTypes = ({ weeklyHoursSchedule, agreementType, monthDate }: MonthContractTypesInput) => {
    const rowContractTypes = getTypedRowsCoveringMonth(weeklyHoursSchedule, monthDate).map(
        (scheduleRow) => CONTRACT_TYPE_BY_WEEKLY_HOURS_TYPE[scheduleRow.agreement_type ?? ""],
    );
    if (rowContractTypes.length > 0) {
        const monthContractTypes = uniq(rowContractTypes);
        return monthContractTypes;
    }
    const agreementContractType = CONTRACT_TYPE_BY_AGREEMENT_TYPE[agreementType ?? ""];
    const fallbackContractTypes: Array<MonthContractType> = agreementContractType ? [agreementContractType] : [];
    return fallbackContractTypes;
};

export const isMixedContractMonth = (monthContractTypes: Array<MonthContractType>) => {
    const isMixed = monthContractTypes.length > 1;
    return isMixed;
};

export const isServiceProviderMonth = (monthContractTypes: Array<MonthContractType>) => {
    const isProviderMonth = monthContractTypes.includes("services_provider");
    return isProviderMonth;
};

// Recibos verdes and cash months have no meal subsidy and no hours balance; a month of unknown type keeps them.
export const isNonContractMonth = (monthContractTypes: Array<MonthContractType>) => {
    const isNonContract = monthContractTypes.length > 0 && !monthContractTypes.includes("contract");
    return isNonContract;
};

const formatDayAndMonth = (dateKey: string) => {
    const dayAndMonth = `${dateKey.slice(8, 10)}/${dateKey.slice(5, 7)}`;
    return dayAndMonth;
};

const describeRowDaysInMonth = (scheduleRow: StaffWeeklyHours, monthDate: Date) => {
    const { monthStartKey, monthEndKey } = getMonthDateKeys(monthDate);
    const { rowStartKey, rowEndKey } = getRowDateKeys(scheduleRow);
    const startsInsideMonth = rowStartKey > monthStartKey;
    const endsInsideMonth = rowEndKey !== null && rowEndKey < monthEndKey;
    if (startsInsideMonth && endsInsideMonth) {
        const bothDays = `de ${formatDayAndMonth(rowStartKey)} a ${formatDayAndMonth(rowEndKey)}`;
        return bothDays;
    }
    if (startsInsideMonth) {
        const firstDay = `desde ${formatDayAndMonth(rowStartKey)}`;
        return firstDay;
    }
    if (endsInsideMonth) {
        const lastDay = `até ${formatDayAndMonth(rowEndKey)}`;
        return lastDay;
    }
    const wholeMonth = "";
    return wholeMonth;
};

export const getMixedContractMonthWarning = ({ weeklyHoursSchedule, monthDate, agreementTypeLabels }: MixedContractMonthWarningInput) => {
    const rowDescriptions = getTypedRowsCoveringMonth(weeklyHoursSchedule, monthDate).map((scheduleRow) => {
        const agreementType = scheduleRow.agreement_type ?? "";
        const label = agreementTypeLabels[agreementType] ?? agreementType;
        const rowDescription = [label, describeRowDaysInMonth(scheduleRow, monthDate)].filter(Boolean).join(" ");
        return rowDescription;
    });
    const warning = `Mês com diferentes tipos de contrato (${rowDescriptions.join(", ")}) — calcular manualmente`;
    return warning;
};
